import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router';

import { type ClientSector } from '@/content/clients';
import { useSubmitEnquiry } from '@/hooks/useSubmitEnquiry';
import {
  fleetEnquiryDefaults,
  fleetEnquirySchema,
  type FleetEnquiryValues,
} from '@/schemas/enquiry';

/**
 * State and submission for the organisation enquiry on /clients. The organism
 * that renders it holds no logic of its own, as `useHomeLeadForm` does for the
 * home page's short form.
 *
 * The enquiry is sent as `general_inquiry` — an existing kind, so no migration
 * and no second queue — and everything that makes it a fleet request travels
 * in `details`, which `submit_website_enquiry` stores as given. The team sees
 * the organisation, the sector, what is wanted and how many in the same place
 * they see every other lead.
 */
export function useFleetEnquiryForm() {
  const { pathname, search } = useLocation();
  const submit = useSubmitEnquiry();
  const { control, register, handleSubmit, setValue } = useForm<FleetEnquiryValues>({
    resolver: zodResolver(fleetEnquirySchema),
    defaultValues: fleetEnquiryDefaults,
  });

  const onSubmit = handleSubmit((values) => {
    submit.mutate({
      kind: 'general_inquiry',
      fullName: values.fullName,
      phone: values.phone,
      email: values.email,
      ...(values.message ? { message: values.message } : {}),
      details: {
        organisation: values.organisation,
        sector: values.sector ?? '',
        vehicles_needed: values.vehiclesNeeded,
        fleet_size: values.fleetSize ?? '',
      },
      pagePath: pathname + search,
      ...(values.companyWebsite ? { companyWebsite: values.companyWebsite } : {}),
    });
  });

  return {
    control,
    register,
    onSubmit,
    /**
     * Fills the sector in from the roster's filter.
     *
     * Called only from the roster's own call to action, which is a deliberate
     * limit: it writes into a field the visitor may already have answered, so
     * the one thing that may trigger it is an explicit tap on a button that
     * names that sector. Nothing watches the filter and syncs it — a reader
     * flicking through chips must not have their form rewritten underneath
     * them, which is the same rule `useQuoteForm` keeps about late-arriving
     * cars.
     */
    prefillSector: (sector: ClientSector) => setValue('sector', sector),
    isPending: submit.isPending,
    error: submit.error,
    receipt: submit.data?.referenceNo ?? null,
  };
}

export type FleetEnquiryFormState = ReturnType<typeof useFleetEnquiryForm>;
